import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, Filter, Search, Download, Eye, Edit, Trash2,
  Clock, CheckCircle2, AlertTriangle, X, Calendar,
  User, Building2, DollarSign, Package, ChevronRight,
  FileText, Send, Users, Tag, Star, Phone, Mail,
  TrendingUp, TrendingDown, Award, FileCheck, Receipt,
  Truck, Route as RouteIcon, ArrowLeftRight, MapPin, Timer, 
  CheckSquare, XCircle, RefreshCw, Navigation
} from 'lucide-react';

const LogisticsTab = ({ timeRange, region }) => {
  const [activeSubTab, setActiveSubTab] = useState('inbound');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const subTabs = [
    { id: 'inbound', label: 'Inbound', icon: ArrowLeftRight, description: 'Shipment receiving & GRN' },
    { id: 'outbound', label: 'Outbound', icon: RouteIcon, description: 'Delivery orders & route planning' },
    { id: 'returns', label: 'Returns', icon: RefreshCw, description: 'Returns & credit notes' }
  ];

  // Mock data for inbound shipments
  const inboundShipments = [
    {
      id: 'IN-2024-001',
      supplier: 'Al-Rashid Trading Co.',
      status: 'in-transit',
      expectedArrival: '2024-03-18',
      items: 45,
      totalValue: 125000,
      trackingNumber: 'TRK123456789',
      carrier: 'Saudi Post',
      warehouse: 'Riyadh Central',
      priority: 'high'
    },
    {
      id: 'IN-2024-002',
      supplier: 'Tech Solutions Ltd.',
      status: 'received',
      expectedArrival: '2024-03-15',
      items: 12,
      totalValue: 89000,
      trackingNumber: 'TRK987654321',
      carrier: 'Aramex',
      warehouse: 'Jeddah Port',
      priority: 'medium'
    }
  ];

  // Mock data for outbound deliveries
  const outboundDeliveries = [
    {
      id: 'OUT-2024-001',
      destination: 'Store #123 - Riyadh Central',
      status: 'in-transit',
      expectedDelivery: '2024-03-19',
      items: 23,
      totalValue: 45000,
      driver: 'Ahmed Al-Zahrani',
      vehicle: 'Truck #R-001',
      route: 'Riyadh → Central District'
    },
    {
      id: 'OUT-2024-002',
      destination: 'Store #145 - Jeddah Beach',
      status: 'delivered',
      expectedDelivery: '2024-03-16',
      items: 18,
      totalValue: 32000,
      driver: 'Mohammed Al-Sayed',
      vehicle: 'Truck #J-002',
      route: 'Jeddah → Beach District'
    }
  ];

  // Mock data for returns
  const returns = [
    {
      id: 'RET-2024-001',
      origin: 'Store #123 - Riyadh Central',
      reason: 'Damaged Goods',
      status: 'pending',
      items: 5,
      totalValue: 8500,
      returnDate: '2024-03-17',
      authorization: 'AUTH-001',
      type: 'replacement'
    },
    {
      id: 'RET-2024-002',
      origin: 'Store #145 - Jeddah Beach',
      reason: 'Wrong Item Received',
      status: 'approved',
      items: 3,
      totalValue: 4200,
      returnDate: '2024-03-15',
      authorization: 'AUTH-002',
      type: 'credit'
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'in-transit': 'bg-blue-100 text-blue-800',
      'received': 'bg-green-100 text-green-800',
      'delivered': 'bg-green-100 text-green-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'processing': 'bg-purple-100 text-purple-800'
    };
    return colors[status] || colors.pending;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-red-100 text-red-800'
    };
    return colors[priority] || colors.medium;
  };

  const renderInbound = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Inbound Logistics</h3>
          <p className="text-sm text-gray-500 mt-1">Shipment receiving, GRN, and supplier delivery tracking</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
          <Plus className="w-4 h-4" />
          New Shipment
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'In Transit', value: inboundShipments.filter(s => s.status === 'in-transit').length, color: 'blue' },
          { label: 'Received Today', value: 3, color: 'green' },
          { label: 'Pending GRN', value: 2, color: 'yellow' },
          { label: 'Total Value', value: `SAR ${inboundShipments.reduce((sum, s) => sum + s.totalValue, 0).toLocaleString()}`, color: 'purple' }
        ].map((stat, index) => (
          <motion.div
            key={index}
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-lg bg-${stat.color}-100`}>
                <Truck className={`w-6 h-6 text-${stat.color}-600`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Shipments Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Shipment Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Supplier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expected Arrival
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {inboundShipments.map((shipment) => (
                <tr key={shipment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg ${getPriorityColor(shipment.priority).split(' ')[0]}`}>
                        <Package className={`w-4 h-4 ${getPriorityColor(shipment.priority).split(' ')[1]}`} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{shipment.id}</p>
                        <p className="text-xs text-gray-500">Tracking: {shipment.trackingNumber}</p>
                        <p className="text-xs text-gray-500">{shipment.items} items</p>
                        <p className="text-xs text-gray-500">Carrier: {shipment.carrier}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">{shipment.supplier}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(shipment.status)}`}>
                      {shipment.status.replace('-', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">
                      {new Date(shipment.expectedArrival).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-gray-900">
                      SAR {shipment.totalValue.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                        <CheckSquare className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors">
                        <Navigation className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderOutbound = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Outbound Logistics</h3>
          <p className="text-sm text-gray-500 mt-1">Delivery orders, route planning, and proof of delivery</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
          <Plus className="w-4 h-4" />
          New Delivery
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'In Transit', value: outboundDeliveries.filter(d => d.status === 'in-transit').length, color: 'blue' },
          { label: 'Delivered Today', value: 5, color: 'green' },
          { label: 'Active Routes', value: 8, color: 'purple' },
          { label: 'Total Value', value: `SAR ${outboundDeliveries.reduce((sum, d) => sum + d.totalValue, 0).toLocaleString()}`, color: 'orange' }
        ].map((stat, index) => (
          <motion.div
            key={index}
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-lg bg-${stat.color}-100`}>
                                        <RouteIcon className={`w-6 h-6 text-${stat.color}-600`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Deliveries Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Delivery Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Destination
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Driver & Vehicle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expected Delivery
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {outboundDeliveries.map((delivery) => (
                <tr key={delivery.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{delivery.id}</p>
                      <p className="text-xs text-gray-500">{delivery.items} items</p>
                      <p className="text-xs text-gray-500">SAR {delivery.totalValue.toLocaleString()}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">{delivery.destination}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(delivery.status)}`}>
                      {delivery.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm text-gray-900">{delivery.driver}</p>
                      <p className="text-xs text-gray-500">{delivery.vehicle}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">
                      {new Date(delivery.expectedDelivery).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                        <RouteIcon className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors">
                        <CheckSquare className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderReturns = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Returns Management</h3>
          <p className="text-sm text-gray-500 mt-1">Branch/customer returns, authorization, and credit notes</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
          <Plus className="w-4 h-4" />
          New Return
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Pending Returns', value: returns.filter(r => r.status === 'pending').length, color: 'yellow' },
          { label: 'Approved Today', value: 3, color: 'green' },
          { label: 'Total Value', value: `SAR ${returns.reduce((sum, r) => sum + r.totalValue, 0).toLocaleString()}`, color: 'red' },
          { label: 'Avg Processing Time', value: '2.5 days', color: 'blue' }
        ].map((stat, index) => (
          <motion.div
            key={index}
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-lg bg-${stat.color}-100`}>
                <RefreshCw className={`w-6 h-6 text-${stat.color}-600`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Returns Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Return Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Origin
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {returns.map((returnItem) => (
                <tr key={returnItem.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{returnItem.id}</p>
                      <p className="text-xs text-gray-500">Auth: {returnItem.authorization}</p>
                      <p className="text-xs text-gray-500">{returnItem.items} items</p>
                      <p className="text-xs text-gray-500">Reason: {returnItem.reason}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Building2 className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">{returnItem.origin}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(returnItem.status)}`}>
                      {returnItem.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      returnItem.type === 'replacement' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {returnItem.type}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-gray-900">
                      SAR {returnItem.totalValue.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                        <CheckSquare className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex space-x-1">
          {subTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeSubTab === tab.id
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title={tab.description}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content based on active sub-tab */}
      {activeSubTab === 'inbound' && renderInbound()}
      {activeSubTab === 'outbound' && renderOutbound()}
      {activeSubTab === 'returns' && renderReturns()}
    </div>
  );
};

export default LogisticsTab; 